/**
 * Add-ons Storefront (Phase 1: Add-ons)
 *
 * Public catalog of active add-ons grouped by family. Signed-in users can buy
 * instant add-ons (purchase + pay) or request a quote for quote-based add-ons,
 * and review their own purchases in the "My add-ons" section.
 *
 * Each card is an anchor, `#addon-<slug>`: the market console's unlock offer links to
 * `/addons#addon-market_insights`, and the page scrolls to (and marks) that card once
 * the catalogue has loaded. A purchase can unlock a gated feature, so every successful
 * one drops the cached entitlements (hooks/useEntitlements).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Coins, Loader2, Package, Users, Gift } from 'lucide-react';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import CoinBalanceBadge from '../../components/coins/CoinBalanceBadge';
import { useAuth } from '../../hooks/useAuth';
import { invalidateEntitlements } from '../../hooks/useEntitlements';
import { useToast } from '../../components/common/ToastProvider';
import { ApiError } from '../../utils/apiClient';
import { formatAmount } from '../../utils/numberFormat';
import { getCoinRate, DEFAULT_COIN_RATE } from '../../services/settingsService';
import {
  fetchAddons,
  fetchMyAddonPurchases,
  fetchMyAddonDiscount,
  purchaseAddon,
  payAddonPurchase,
  type Addon,
  type AddonPurchase,
  type AddonFamily,
} from '../../services/addonService';
import { fetchBundles, purchaseBundle, type AddonBundle } from '../../services/bundleService';
import BundleCard from '../../components/bundles/BundleCard';
import SignInPrompt from '../../components/common/SignInPrompt';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const FAMILY_ORDER: AddonFamily[] = [
  'VISIBILITY_LIFECYCLE',
  'LISTING_ENRICHMENT',
  'AI_STUDIO',
  'TRUST_VERIFICATION',
  'PROFESSIONAL_CONTENT',
  'PERSONAL',
  'OTHER',
];

/** The anchor id of an add-on's card: /addons#addon-<slug>. */
const addonAnchor = (slug: string) => `addon-${slug}`;

/** The add-on slug a `#addon-<slug>` hash points at, or null. */
function anchoredSlug(hash: string): string | null {
  let id = hash.replace(/^#/, '');
  try {
    id = decodeURIComponent(id);
  } catch {
    return null;
  }
  return id.startsWith('addon-') && id.length > 'addon-'.length ? id.slice('addon-'.length) : null;
}

export default function AddonsStorefrontPage() {
  const { t, i18n } = useTranslation('addons');
  // Bundles live in their own i18n namespace.
  const { t: tb } = useTranslation('bundles');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const targetSlug = anchoredSlug(location.hash);
  // The hash a card was last scrolled to, so a later reload of the catalogue (sign-in,
  // a purchase) does not yank the page back to it.
  const scrolledFor = useRef<string | null>(null);

  const [addons, setAddons] = useState<Addon[]>([]);
  const [bundles, setBundles] = useState<AddonBundle[]>([]);
  const [purchases, setPurchases] = useState<AddonPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  // Bundle purchases use a separate pending key (bundle slugs may collide with add-on slugs).
  const [pendingBundleSlug, setPendingBundleSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // SAR-per-coin rate, used to derive a coin price when the add-on lacks one.
  const [coinRate, setCoinRate] = useState<number>(DEFAULT_COIN_RATE);
  // Buyer's plan add-on discount (%), derived from their active subscription
  // plan. 0 when signed out or with no active plan. The backend applies this at
  // purchase; here we only mirror it in the displayed prices.
  const [addonDiscount, setAddonDiscount] = useState<number>(0);
  // Non-refundable purchase confirmation (gates every paid action).
  const [confirmAction, setConfirmAction] = useState<null | { message: string; run: () => Promise<void> }>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const runConfirm = useCallback(async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      await confirmAction.run();
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }, [confirmAction]);

  const localizedName = useCallback(
    (addon: Pick<Addon, 'name' | 'nameAr'>) =>
      isRTL ? addon.nameAr || addon.name : addon.name,
    [isRTL],
  );

  const localizedDescription = useCallback(
    (addon: Pick<Addon, 'description' | 'descriptionAr'>) =>
      isRTL ? addon.descriptionAr || addon.description : addon.description,
    [isRTL],
  );

  const loadPurchases = useCallback(async () => {
    if (!user) {
      setPurchases([]);
      return;
    }
    try {
      const response = await fetchMyAddonPurchases({ pageSize: 50 });
      setPurchases(response.data);
    } catch (err) {
      console.error('Failed to fetch add-on purchases:', err);
    }
  }, [user]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [catalog, bundleList, rate, discount] = await Promise.all([
        fetchAddons(),
        fetchBundles().catch((err) => {
          console.error('Failed to load bundles:', err);
          return [] as AddonBundle[];
        }),
        getCoinRate(),
        // Only signed-in buyers have a plan discount; skip the call otherwise.
        user ? fetchMyAddonDiscount() : Promise.resolve(0),
      ]);
      setAddons(catalog);
      setBundles(bundleList);
      setCoinRate(rate);
      setAddonDiscount(discount);
      await loadPurchases();
    } catch (err) {
      console.error('Failed to load add-ons:', err);
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [loadPurchases, t, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Deep link to one add-on: once its card is on the page, bring it into view and move
  // focus to its title, once per hash. A card that is not in the catalogue (switched
  // off) leaves the page where it is.
  useEffect(() => {
    if (isLoading || !targetSlug) return;
    const id = addonAnchor(targetSlug);
    if (scrolledFor.current === id) return;
    const card = document.getElementById(id);
    if (!card) return;
    scrolledFor.current = id;
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    document.getElementById(`${id}-title`)?.focus({ preventScroll: true });
  }, [isLoading, targetSlug]);

  const handleBuy = async (addon: Addon) => {
    try {
      setPendingSlug(addon.slug);
      setError(null);
      const { data } = await purchaseAddon(addon.slug, {});
      // Instant dev path: pay immediately to activate.
      await payAddonPurchase(data.id);
      // It may unlock a gated feature («مؤشرات السوق»): drop the cached access answer.
      invalidateEntitlements();
      showToast(t('purchaseSuccess'), 'success');
      await loadPurchases();
    } catch (err) {
      console.error('Failed to buy add-on:', err);
      showToast(t('actionError'), 'error');
    } finally {
      setPendingSlug(null);
    }
  };

  const handleRequest = async (addon: Addon) => {
    try {
      setPendingSlug(addon.slug);
      setError(null);
      await purchaseAddon(addon.slug, {});
      showToast(t('requestReceived'), 'success');
      await loadPurchases();
    } catch (err) {
      console.error('Failed to request add-on:', err);
      showToast(t('actionError'), 'error');
    } finally {
      setPendingSlug(null);
    }
  };

  // Apply the buyer's plan discount to a list SAR price (matches the backend).
  const discounted = useCallback(
    (listSar: number): number => listSar - Math.floor((listSar * addonDiscount) / 100),
    [addonDiscount],
  );

  // Coin price for an add-on: its explicit priceCoins, otherwise derived from
  // the discounted SAR price at the current rate (mirrors the backend formula).
  const coinPriceOf = useCallback(
    (addon: Addon): number | null => {
      if (addon.priceCoins !== undefined && addon.priceCoins !== null) {
        return addon.priceCoins;
      }
      if (addon.priceSar !== undefined && addon.priceSar !== null && coinRate > 0) {
        return Math.ceil(discounted(addon.priceSar) / coinRate);
      }
      return null;
    },
    [coinRate, discounted],
  );

  const handleBuyWithCoins = async (addon: Addon) => {
    try {
      setPendingSlug(addon.slug);
      setError(null);
      // Coins path: the backend returns an already-ACTIVE purchase, so there is
      // no separate pay step (unlike the SAR flow).
      await purchaseAddon(addon.slug, { pricingMethod: 'COINS' });
      invalidateEntitlements();
      showToast(t('payWithCoinsSuccess'), 'success');
      await loadPurchases();
    } catch (err) {
      console.error('Failed to pay for add-on with coins:', err);
      const code =
        err instanceof ApiError &&
        err.details &&
        typeof err.details === 'object' &&
        'code' in err.details
          ? (err.details as { code?: string }).code
          : undefined;
      if (
        (err instanceof ApiError && err.status === 409) ||
        code === 'INSUFFICIENT_COINS'
      ) {
        showToast(t('insufficientCoins'), 'error');
      } else {
        showToast(t('actionError'), 'error');
      }
    } finally {
      setPendingSlug(null);
    }
  };

  const handleBuyBundle = async (bundle: AddonBundle, method: 'SAR' | 'COINS') => {
    try {
      setPendingBundleSlug(bundle.slug);
      setError(null);
      // Both SAR and COINS paths return an already-ACTIVE bundle purchase.
      await purchaseBundle(bundle.slug, { pricingMethod: method });
      invalidateEntitlements();
      showToast(
        method === 'COINS' ? tb('payWithCoinsSuccess') : tb('purchaseSuccess'),
        'success',
      );
      await loadPurchases();
    } catch (err) {
      console.error('Failed to buy bundle:', err);
      const code =
        err instanceof ApiError &&
        err.details &&
        typeof err.details === 'object' &&
        'code' in err.details
          ? (err.details as { code?: string }).code
          : undefined;
      if (
        method === 'COINS' &&
        ((err instanceof ApiError && err.status === 409) || code === 'INSUFFICIENT_COINS')
      ) {
        showToast(tb('insufficientCoins'), 'error');
      } else {
        showToast(tb('actionError'), 'error');
      }
    } finally {
      setPendingBundleSlug(null);
    }
  };

  const formatPrice = (addon: Addon) => {
    if (addon.isQuoteBased) return t('quoteBased');
    if (addon.priceSar !== undefined && addon.priceSar !== null) {
      return t('priceSar', { price: formatAmount(addon.priceSar) });
    }
    return t('free');
  };

  // Group add-ons by family, preserving the canonical family order.
  const grouped = FAMILY_ORDER.map((family) => ({
    family,
    items: addons.filter((a) => a.family === family),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-3">
            {t('pageTitle')}
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto">{t('pageSubtitle')}</p>
          {user && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
              <span>{t('yourBalance')}</span>
              <CoinBalanceBadge />
            </div>
          )}
          {addonDiscount > 0 && (
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-sm font-medium text-[#003930] dark:text-[#B7DDD7]">
              <Gift className="w-4 h-4" aria-hidden="true" />
              {t('planDiscountNote', { percent: addonDiscount })}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {!user && <SignInPrompt message={t('signInToBuy')} className="mb-8" />}

        {/* Bundles */}
        {!isLoading && bundles.length > 0 && (
          <section className="mb-12">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] dark:text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#004D40]" />
                {tb('sectionTitle')}
              </h2>
              <p className="text-sm text-gray-500">{tb('sectionSubtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bundles.map((bundle) => (
                <BundleCard
                  key={bundle.id}
                  bundle={bundle}
                  coinRate={coinRate}
                  addonDiscount={addonDiscount}
                  isAuthed={!!user}
                  isPending={pendingBundleSlug === bundle.slug}
                  onBuy={(method) =>
                    setConfirmAction({
                      message: t('confirm.message'),
                      run: () => handleBuyBundle(bundle, method),
                    })
                  }
                />
              ))}
            </div>
          </section>
        )}

        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
          </div>
        ) : grouped.length === 0 ? (
          bundles.length === 0 ? (
            <div className="py-20 text-center text-gray-500">{t('catalogEmpty')}</div>
          ) : null
        ) : (
          <div className="space-y-12">
            {grouped.map((group) => (
              <section key={group.family}>
                <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] dark:text-white mb-5">
                  {t(`family.${group.family}`)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.items.map((addon) => {
                    const isPending = pendingSlug === addon.slug;
                    const coinPrice = addon.isQuoteBased ? null : coinPriceOf(addon);
                    const anchor = addonAnchor(addon.slug);
                    // The card a deep link points at (/addons#addon-<slug>) carries a teal ring.
                    const isTarget = targetSlug === addon.slug;
                    return (
                      <div
                        key={addon.id}
                        id={anchor}
                        className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 flex flex-col shadow-sm hover:shadow-md transition-shadow scroll-mt-28${
                          isTarget
                            ? ' ring-2 ring-[var(--pv-color-primary)] ring-offset-2 dark:ring-offset-gray-900'
                            : ''
                        }`}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-[#004D40]" />
                          </div>
                          <h3
                            id={`${anchor}-title`}
                            tabIndex={-1}
                            className="font-semibold text-gray-900 dark:text-white focus:outline-none"
                          >
                            {localizedName(addon)}
                          </h3>
                        </div>

                        {localizedDescription(addon) && (
                          <p className="text-sm text-gray-500 mb-4 flex-1">
                            {localizedDescription(addon)}
                          </p>
                        )}

                        {/* Audiences */}
                        <div className="flex items-center flex-wrap gap-1.5 mb-4">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          {addon.audiences.map((aud) => (
                            <span
                              key={aud}
                              className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                            >
                              {t(`audience.${aud}`)}
                            </span>
                          ))}
                        </div>

                        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-2 border-t dark:border-gray-700">
                          {!addon.isQuoteBased &&
                          addon.priceSar !== undefined &&
                          addon.priceSar !== null &&
                          addonDiscount > 0 ? (
                            <span className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-sm text-gray-400 line-through">
                                {t('priceSar', { price: formatAmount(addon.priceSar) })}
                              </span>
                              <span className="text-lg font-bold text-[#004D40]">
                                {t('priceSar', {
                                  price: formatAmount(discounted(addon.priceSar)),
                                })}
                              </span>
                            </span>
                          ) : (
                            <span className="text-lg font-bold text-[#004D40]">
                              {formatPrice(addon)}
                            </span>
                          )}
                          {user ? (
                            addon.isQuoteBased ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRequest(addon)}
                                loading={isPending}
                              >
                                {t('request')}
                              </Button>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    setConfirmAction({
                                      message: t('confirm.message'),
                                      run: () => handleBuy(addon),
                                    })
                                  }
                                  loading={isPending}
                                  disabled={isPending}
                                >
                                  {t('buy')}
                                </Button>
                                {coinPrice !== null && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setConfirmAction({
                                        message: t('confirm.coinsMessage', { count: coinPrice }),
                                        run: () => handleBuyWithCoins(addon),
                                      })
                                    }
                                    loading={isPending}
                                    disabled={isPending}
                                  >
                                    <Coins className="w-3.5 h-3.5" aria-hidden="true" />
                                    {t('coinPrice', { count: coinPrice })}
                                  </Button>
                                )}
                              </div>
                            )
                          ) : (
                            // Back to this page after signing in.
                            <Link to="/signin" state={{ from: location }}>
                              <Button size="sm" variant="outline">
                                {addon.isQuoteBased ? t('request') : t('buy')}
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* My add-ons */}
        {user && (
          <section className="mt-16">
            <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] dark:text-white mb-5">
              {t('myAddonsTitle')}
            </h2>
            {purchases.length === 0 ? (
              <p className="text-gray-500">{t('myAddonsEmpty')}</p>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 divide-y dark:divide-gray-700 overflow-hidden">
                {purchases.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="flex items-center justify-between gap-4 p-4"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {purchase.addon
                          ? localizedName(purchase.addon)
                          : t('pageTitle')}
                      </p>
                      {purchase.priceSar > 0 && (
                        <p className="text-sm text-gray-500">
                          {t('priceSar', { price: formatAmount(purchase.priceSar) })}
                        </p>
                      )}
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {t(`status.${purchase.status}`)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <Footer />

      <ConfirmDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirm}
        title={t('confirm.title')}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('confirm.cta')}
        cancelLabel={t('cancel')}
        isLoading={confirmLoading}
      />
    </div>
  );
}
